import { DsButton } from '@/modules/internal-v2/ds';
import type {
  ResourceType,
  ReviewVocabularyEntry,
} from '@/api/specifications/specifications.types';
import { ProposalRow } from './proposal-row.component';
import type { MergeTarget, Selection } from './selection.types';

interface VocabularySectionProps {
  groups: Record<string, ReviewVocabularyEntry[]>;
  isLoading: boolean;
  canManage: boolean;
  selection: Selection;
  onSelectChange: (key: string, next: boolean) => void;
  onSelectMany: (entries: { key: string; selected: boolean }[]) => void;
  onApprove: (resource: ResourceType, id: number) => void;
  onReject: (resource: ResourceType, id: number) => void;
  onOpenMerge: (target: MergeTarget) => void;
}

export function VocabularySection({
  groups,
  isLoading,
  canManage,
  selection,
  onSelectChange,
  onSelectMany,
  onApprove,
  onReject,
  onOpenMerge,
}: VocabularySectionProps) {
  const attributes = Object.keys(groups).sort();

  if (isLoading && attributes.length === 0) {
    return <div className="sp-empty">Lade Vorschläge…</div>;
  }
  if (!isLoading && attributes.length === 0) {
    return (
      <div className="sp-empty">
        <div className="big">Keine offenen Vokabular-Vorschläge</div>
        <div>Alles freigegeben — oder Filter zu eng.</div>
      </div>
    );
  }

  return (
    <>
      {attributes.map((attr) => {
        const entries = groups[attr];
        const pendingKeys = entries
          .filter((v) => v.state === 'proposed')
          .map((v) => `vocabulary:${v.id}`);
        const allSelected =
          pendingKeys.length > 0 && pendingKeys.every((k) => selection.has(k));

        return (
          <section key={attr} className="sp-section">
            <header className="sp-section-head">
              <div className="sp-section-title">
                <span className="sp-attr">{attr}</span>
              </div>
              <div className="sp-section-meta">
                {entries.length} Werte
              </div>
              {canManage && pendingKeys.length > 0 && (
                <div className="sp-section-actions">
                  <DsButton
                    variant="ghost"
                    onClick={() =>
                      onSelectMany(
                        pendingKeys.map((k) => ({ key: k, selected: !allSelected })),
                      )
                    }
                  >
                    {allSelected ? 'Auswahl aufheben' : `Alle ${pendingKeys.length} auswählen`}
                  </DsButton>
                </div>
              )}
            </header>

            {entries.map((v) => {
              const key = `vocabulary:${v.id}`;
              return (
                <ProposalRow
                  key={v.id}
                  selectionKey={key}
                  name={v.value}
                  state={v.state}
                  itemCount={v.itemCount}
                  sampleItems={v.sampleItems}
                  canApprove={v.state === 'proposed'}
                  canManage={canManage}
                  selected={selection.has(key)}
                  onSelectChange={(next) => onSelectChange(key, next)}
                  onApprove={() => onApprove('vocabulary', v.id)}
                  onMerge={() =>
                    onOpenMerge({
                      resource: 'vocabulary',
                      sourceId: v.id,
                      sourceLabel: v.value,
                      scope: { manufacturerId: v.manufacturerId, attribute: attr },
                    })
                  }
                  onReject={() => onReject('vocabulary', v.id)}
                />
              );
            })}
          </section>
        );
      })}
    </>
  );
}
